/**
 * 遺伝子マップの見た目に関わる純ロジック（U150 / U161）。
 * 描画から切り離してテストできるようにする。
 */

import { featureSpans, type GenomicFeatureV1, type Interval } from '@kensuke/genome-core';

/** 塩基の字幅（CSS px）。読める下限と、高解像度でも大きすぎない上限（§0.3.1） */
export const MIN_SEQUENCE_FONT_SIZE = 9;
export const MAX_SEQUENCE_FONT_SIZE = 28;

export interface LanePackingOptions {
  genomeLength: number;
  /** 1 塩基あたりの画面幅（px） */
  pixelsPerBase: number;
  /** 同じレーンに並べるときの最小の隙間（px） */
  minGapPx?: number;
  /** 使えるレーン数の上限。溢れた注釈は落とす */
  maxLanes?: number;
}

export interface PackedFeature {
  feature: GenomicFeatureV1;
  lane: number;
  spans: Interval[];
  /** 画面上の幅（px）。ラベルを出すかの判断に使う */
  widthPx: number;
}

export interface LanePackingResult {
  packed: PackedFeature[];
  laneCount: number;
  /** レーン上限で落とした件数（「ほか N 件」表示に使う） */
  droppedCount: number;
}

/**
 * 注釈をレーンに詰める。左から貪欲に置き、重なるものだけ下のレーンへ送る。
 * 長い順ではなく開始順に置くので、同じ縮尺なら並びが安定する。
 */
export function packFeatureLanes(
  features: GenomicFeatureV1[],
  options: LanePackingOptions
): LanePackingResult {
  const minGapPx = options.minGapPx ?? 4;
  const maxLanes = options.maxLanes ?? 8;
  const packed: PackedFeature[] = [];
  let droppedCount = 0;

  const sorted = features
    .map((feature) => ({ feature, spans: featureSpans(feature, options.genomeLength) }))
    .filter((entry) => entry.spans.length > 0)
    .sort((a, b) => a.spans[0].start - b.spans[0].start);

  // レーンごとの占有区間。原点ラップ注釈は複数スパンを別々に衝突判定する
  const laneOccupancy: Interval[][] = [];

  const fitsInLane = (occupancy: Interval[], spans: Interval[], gapBases: number): boolean =>
    spans.every((span) =>
      occupancy.every((taken) => span.end + gapBases <= taken.start || taken.end + gapBases <= span.start)
    );

  for (const entry of sorted) {
    const gapBases = options.pixelsPerBase > 0 ? minGapPx / options.pixelsPerBase : 0;
    let lane = laneOccupancy.findIndex((occupancy) => fitsInLane(occupancy, entry.spans, gapBases));
    if (lane === -1) {
      if (laneOccupancy.length >= maxLanes) {
        droppedCount += 1;
        continue;
      }
      laneOccupancy.push([]);
      lane = laneOccupancy.length - 1;
    }
    laneOccupancy[lane].push(...entry.spans);

    const widthPx = entry.spans.reduce(
      (sum, span) => sum + Math.max(0, span.end - span.start) * options.pixelsPerBase,
      0
    );

    packed.push({
      feature: entry.feature,
      lane,
      spans: entry.spans,
      widthPx: Math.max(1, widthPx),
    });
  }

  return { packed, laneCount: laneOccupancy.length, droppedCount };
}

/** 帯の中に名前を書けるか（書けないときはホバー／クリックで出す・U161） */
export function canFitLabel(widthPx: number, text: string, charWidthPx: number): boolean {
  return widthPx >= (text.length + 1) * charWidthPx;
}

/** 名前を帯の幅に収まるよう詰める */
export function truncateLabel(text: string, widthPx: number, charWidthPx: number): string {
  const maxChars = Math.floor(widthPx / charWidthPx) - 1;
  if (maxChars <= 0) return '';
  if (text.length <= maxChars) return text;
  return maxChars <= 1 ? text.slice(0, 1) : `${text.slice(0, maxChars - 1)}…`;
}

/** 注釈の型ごとの既定色。色だけに意味を載せない前提で、形（鎖の矢印）と名前を併用する。 */
export const FEATURE_TYPE_COLORS: Record<string, string> = {
  gene: '#2563EB',
  CDS: '#7C3AED',
  mRNA: '#0891B2',
  tRNA: '#059669',
  rRNA: '#0D9488',
  promoter: '#D97706',
  terminator: '#B45309',
  regulatory: '#CA8A04',
  repeat_region: '#78716C',
  primer_bind: '#DB2777',
  misc_feature: '#64748B',
  source: '#94A3B8',
};

export function featureColor(feature: GenomicFeatureV1): string {
  return feature.color ?? FEATURE_TYPE_COLORS[feature.type] ?? FEATURE_TYPE_COLORS.misc_feature;
}

/** 画面 x（CSS px）→ 塩基位置 */
export function pixelToBase(x: number, viewStart: number, pixelsPerBase: number): number {
  return pixelsPerBase > 0 ? viewStart + x / pixelsPerBase : viewStart;
}

/** 塩基位置 → 画面 x（CSS px） */
export function baseToPixel(base: number, viewStart: number, pixelsPerBase: number): number {
  return (base - viewStart) * pixelsPerBase;
}

/** 折返し塩基ビューの行数（可視高さに入るだけ） */
export function visibleLineCount(availableHeight: number, lineHeight: number): number {
  return Math.max(1, Math.floor(availableHeight / Math.max(1, lineHeight)));
}
